import { Component, Inject } from '@angular/core';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, NgIf } from '@angular/common';
import { AiAgentService } from '../../../../services/ai-agent.service';
import { CalComIntegrationResponse } from '../../../../models/responses/calcom-integration-response';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CAL_ICON } from '../../../../app.component';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import * as moment from 'moment-timezone';
import { Observable, map, startWith } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { timezoneValidator } from '../../../../validators/timezone-validator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-cal-integration-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatDivider,
    MatIconModule,
    NgIf,
    MatAutocompleteModule,
    AsyncPipe,
    MatProgressSpinnerModule
  ],
  templateUrl: './cal-integration-dialog.component.html',
  styleUrl: './cal-integration-dialog.component.scss'
})
export class CalIntegrationDialogComponent {
  calForm!: FormGroup;
  isEditing: boolean = false;
  isLoading: boolean = false;
  calIcon: string = CAL_ICON;
  filteredOptions!: Observable<string[]>;
  timeZones: string[] = moment.tz.names();

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<CalIntegrationDialogComponent>,
    private aiAgentService: AiAgentService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, cal: CalComIntegrationResponse },
    private aiConfigurationPageEventsService: AiConfigurationPageEventsService
  ) { }

  ngOnInit() {
    this.initCalIntegrationForm();
  }

  initCalIntegrationForm() {
    const integration: CalComIntegrationResponse = this.data.cal;

    if (integration) this.isEditing = true;

    this.calForm = this.formBuilder.group({
      id: [integration ? integration.id : '-1', Validators.required],
      eventId: [integration ? integration.eventId : '', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      apiKey: [integration ? integration.apiKey : '', Validators.required],
      timezone: [integration ? integration.timezone : '', [Validators.required, timezoneValidator(this.timeZones)]],
      username: [integration ? integration.username : '', Validators.required],
      name: [integration ? integration.name : '', Validators.required]
    });

    this.filteredOptions = this.calForm.controls['timezone'].valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
  }

  onAdd() {
    if (this.calForm.valid) {
      const values = this.calForm.value;

      const calIntegration: CalComIntegrationResponse = {
        id: parseInt(values['id']),
        name: values['name'],
        eventId: parseInt(values['eventId']),
        apiKey: values['apiKey'],
        timezone: values['timezone'],
        username: values['username'],
        aiAgentIntegrationsId: this.data.aiAgent.integrations.id
      };

      this.isLoading = true;

      this.aiAgentService.addCalComIntegration(calIntegration).subscribe({
        next: (cal) => {
          this.snackBar.open(`Cal integration ${this.isEditing ? 'saved' : 'added'} successfully`, 'Close', { duration: 5000 });
          this.isLoading = false;
          this.dialogRef.close();

          this.aiConfigurationPageEventsService.onCalIntegrationAddedEvent.emit(cal);
        },
        error: () => {
          alert('Error contact support');
          this.isLoading = false;
        }
      });
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.timeZones.filter(option => option.toLowerCase().includes(filterValue));
  }
}
